import React, { useEffect, useState } from 'react'
import { 
  IonContent, 
  IonPage, 
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonButton,
  IonIcon,
  IonGrid,
  IonRow,
  IonCol,
  IonChip,
  IonProgressBar,
  IonFab,
  IonFabButton,
  IonList,
  IonItem,
  IonLabel,
  IonBadge,
  IonButtons
} from '@ionic/react'
import { 
  calendarOutline, 
  trophyOutline, 
  chatbubbleOutline,
  addOutline,
  arrowForwardOutline,
  peopleOutline,
  journalOutline,
  statsChartOutline,
  notificationsOutline,
  colorPaletteOutline,
  ellipsisVerticalOutline,
  linkOutline
} from 'ionicons/icons'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '../../store/authStore'
import { DashboardData, CoachCoacheeRelationship } from '../../types'
import ThemeSwitcher from '../../components/ThemeSwitcher'
import CoachManagement from '../../components/CoachManagement'
import PageHeader from '../../components/PageHeader'
import MotivationalWelcomeHeader from '../../components/MotivationalMessage'
import Avatar from '../../components/Avatar'
import { supabase } from '../../services/supabase'

const DashboardPage: React.FC = () => {
  const { user, isCoach, isCoachee } = useAuthStore()
  const { t } = useTranslation()
  const [dashboardData, setDashboardData] = useState<DashboardData>({})
  const [loading, setLoading] = useState(true)
  const [isThemeSwitcherOpen, setIsThemeSwitcherOpen] = useState(false)
  const [coachRelationships, setCoachRelationships] = useState<(CoachCoacheeRelationship & { 
    coach_name?: string, 
    coach_email?: string,
    coach_specializations?: string[],
    coach_contact_details?: string,
    coach_avatar_url?: string,
    coach_profile_picture_visibility?: string
  })[]>([])
  const [recentInsights, setRecentInsights] = useState<any[]>([])
  const [isCoachManagementModalOpen, setIsCoachManagementModalOpen] = useState(false)

  // Fetch coach relationships for coachees
  const fetchCoachRelationships = async () => {
    if (!user || user.role !== 'coachee') return

    try {
      const { data, error } = await supabase
        .from('coach_coachee_relationships')
        .select(`
          *,
          coach:profiles!coach_coachee_relationships_coach_id_fkey (
            full_name,
            email,
            specializations,
            contact_details,
            avatar_url,
            profile_picture_url,
            profile_picture_visibility
          )
        `)
        .eq('coachee_id', user.id)
        .order('started_at', { ascending: false })

      if (error) {
        console.error('Error fetching coach relationships:', error)
        return
      }

      const relationshipsWithCoachInfo = data?.map((rel: any) => ({
        ...rel,
        coach_name: rel.coach?.full_name || rel.coach?.email || t('dashboard.coach.unknownCoach'),
        coach_email: rel.coach?.email,
        coach_specializations: rel.coach?.specializations,
        coach_contact_details: rel.coach?.contact_details,
        coach_avatar_url: rel.coach?.profile_picture_url || rel.coach?.avatar_url,
        coach_profile_picture_visibility: rel.coach?.profile_picture_visibility
      })) || []

      setCoachRelationships(relationshipsWithCoachInfo)
    } catch (error) {
      console.error('Error fetching coach relationships:', error)
    }
  }

  // Fetch recent insights for coaches
  const fetchRecentInsights = async () => {
    if (!user || user.role !== 'coach') return

    try {
      const { data, error } = await supabase
        .from('reflection_entries')
        .select(`
          *,
          coachee:profiles!reflection_entries_coachee_id_fkey (
            id,
            full_name,
            email,
            profile_picture_url,
            profile_picture_visibility
          )
        `)
        .order('date', { ascending: false })
        .limit(5)

      if (error) {
        console.error('Error fetching recent insights:', error)
        return
      }

      // Filter to only show insights from coachees that the coach has relationships with
      const { data: relationships, error: relError } = await supabase
        .from('coach_coachee_relationships')
        .select('coachee_id')
        .eq('coach_id', user.id)
        .eq('status', 'active')

      if (relError) {
        console.error('Error fetching relationships:', relError)
        return
      }

      const coacheeIds = relationships?.map(rel => rel.coachee_id) || []
      const filteredInsights = data?.filter(insight => coacheeIds.includes(insight.coachee_id)) || []

      setRecentInsights(filteredInsights)
    } catch (error) {
      console.error('Error fetching recent insights:', error)
    }
  }

  // Fetch real dashboard data from database
  const fetchDashboardData = async () => {
    if (!user) return

    try {
      setLoading(true)

      if (isCoach()) {
        // Fetch upcoming sessions for coaches
        const { data: sessions, error: sessionsError } = await supabase
          .from('coaching_sessions')
          .select(`
            *,
            coachee:profiles!coaching_sessions_coachee_id_fkey (
              id,
              full_name,
              email,
              profile_picture_url,
              profile_picture_visibility
            )
          `)
          .eq('coach_id', user.id)
          .in('status', ['scheduled', 'in_progress'])
          .gte('date', new Date().toISOString().split('T')[0])
          .order('date', { ascending: true })
          .order('start_time', { ascending: true })
          .limit(5)

        if (sessionsError) throw sessionsError

        setDashboardData({
          upcomingSessions: sessions || [],
          unreadMessages: [] // TODO: Implement messaging system
        })

      } else if (isCoachee()) {
        // Fetch recent reflection entries, active goals, and upcoming sessions for coachees
        const [reflectionsResponse, goalsResponse, sessionsResponse] = await Promise.all([
          supabase
            .from('reflection_entries')
            .select('*')
            .eq('coachee_id', user.id)
            .order('date', { ascending: false })
            .limit(3),
          supabase
            .from('goals')
            .select('*')
            .eq('user_id', user.id)
            .eq('status', 'active')
            .order('created_at', { ascending: false })
            .limit(3),
          supabase
            .from('coaching_sessions')
            .select(`
              *,
              coach:profiles!coaching_sessions_coach_id_fkey (
                id,
                full_name,
                email,
                profile_picture_url,
                profile_picture_visibility
              )
            `)
            .eq('coachee_id', user.id)
            .in('status', ['scheduled', 'in_progress'])
            .gte('date', new Date().toISOString().split('T')[0])
            .order('date', { ascending: true })
            .order('start_time', { ascending: true })
            .limit(5)
        ])

        if (reflectionsResponse.error) throw reflectionsResponse.error
        if (goalsResponse.error) throw goalsResponse.error
        if (sessionsResponse.error) throw sessionsResponse.error

        setDashboardData({
          recentEntries: reflectionsResponse.data || [],
          activeGoals: goalsResponse.data || [],
          upcomingSessions: sessionsResponse.data || [],
          unreadMessages: [] // TODO: Implement messaging system
        })
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (user) {
      fetchDashboardData()
      if (isCoachee()) {
        fetchCoachRelationships()
      }
      if (isCoach()) {
        fetchRecentInsights()
      }
    }
  }, [user, isCoach, isCoachee])

  const renderCoachDashboard = () => (
    <>
      {/* Integrated Welcome Header with Motivational Message */}
      <MotivationalWelcomeHeader 
        className="mb-4"
        additionalInfo={t('dashboard.coach.additionalInfo', { 
          sessions: dashboardData.upcomingSessions?.length || 0,
          messages: dashboardData.unreadMessages?.length || 0
        })}
      />

      <IonGrid className="px-0">
        <IonRow>
          {/* Coach Management */}
          <IonCol size="12" className="px-2 sm:px-3">
            <CoachManagement 
              isModalOpen={isCoachManagementModalOpen}
              onModalOpenChange={setIsCoachManagementModalOpen}
            />
          </IonCol>
        </IonRow>

        <IonRow>
          {/* Upcoming Sessions */}
          <IonCol size="12" sizeLg="6" className="px-2 sm:px-3">
            <IonCard className="h-full">
              <IonCardHeader>
                <IonCardTitle className="flex items-center justify-between text-base sm:text-lg">
                  <div className="flex items-center">
                    <IonIcon icon={calendarOutline} className="mr-2 flex-shrink-0" />
                    <span className="break-words">{t('dashboard.coach.upcomingSessions')}</span>
                  </div>
                  <IonButton 
                    fill="clear" 
                    size="small"
                    routerLink="/app/sessions"
                  >
                    <IonIcon icon={addOutline} slot="start" className="hidden sm:block" />
                    <IonIcon icon={ellipsisVerticalOutline} className="sm:hidden" />
                    <span className="hidden sm:inline ml-2">{t('dashboard.coach.viewAllSessions')}</span>
                  </IonButton>
                </IonCardTitle>
              </IonCardHeader>
              <IonCardContent>
                {dashboardData.upcomingSessions?.length ? (
                  <div className="space-y-3">
                    {dashboardData.upcomingSessions.slice(0, 3).map((session) => (
                      <div key={session.id} className="bg-primary-50 p-3 rounded-lg">
                        <div className="flex items-start justify-between mb-2">
                          <h4 className="font-medium text-primary-900 text-sm sm:text-base break-words leading-tight flex-1">{session.title}</h4>
                          <IonChip 
                            color={session.status === 'scheduled' ? 'primary' : 'warning'} 
                            className="text-xs ml-2 flex-shrink-0"
                          >
                            {session.status.replace('_', ' ')}
                          </IonChip>
                        </div>
                        <div className="flex items-center mb-2">
                          <Avatar
                            user={{
                              id: (session as any).coachee?.id || session.coachee_id,
                              full_name: (session as any).coachee?.full_name || 'Unknown',
                              email: (session as any).coachee?.email || '',
                              profile_picture_url: (session as any).coachee?.profile_picture_url,
                              profile_picture_visibility: (session as any).coachee?.profile_picture_visibility as any
                            }}
                            currentUserId={user?.id}
                            relationship="coach"
                            size="small"
                            className="w-6 h-6 mr-2"
                          />
                          <span className="text-xs sm:text-sm text-primary-700 font-medium">
                            {(session as any).coachee?.full_name || 'Unknown Coachee'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <p className="text-xs sm:text-sm text-primary-700">
                            {new Date(session.date).toLocaleDateString()} {t('dashboard.coach.at')} {session.start_time}
                          </p>
                          <IonChip color="primary" className="text-xs">
                            {session.duration_minutes} {t('dashboard.coach.min')}
                          </IonChip>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <IonIcon icon={calendarOutline} className="text-4xl text-gray-400 mb-4" />
                    <p className="text-gray-500 mb-4 text-sm break-words">{t('dashboard.coach.noUpcomingSessions')}</p>
                    <IonButton 
                      fill="outline" 
                      size="small"
                      routerLink="/app/sessions"
                    >
                      <IonIcon icon={addOutline} slot="start" />
                      {t('dashboard.coach.createFirstSession')}
                    </IonButton>
                  </div>
                )}
              </IonCardContent>
            </IonCard>
          </IonCol>

          {/* Latest Insights */}
          <IonCol size="12" sizeLg="6" className="px-2 sm:px-3">
            <IonCard className="h-full">
              <IonCardHeader>
                <IonCardTitle className="flex items-center justify-between text-base sm:text-lg">
                  <div className="flex items-center">
                    <IonIcon icon={journalOutline} className="mr-2 flex-shrink-0" />
                    <span className="break-words">{t('dashboard.coach.latestInsights')}</span>
                  </div>
                  <IonButton 
                    fill="clear" 
                    size="small"
                    routerLink="/app/insights"
                  >
                    <IonIcon icon={ellipsisVerticalOutline} className="sm:hidden" />
                    <span className="hidden sm:inline">{t('dashboard.coach.viewAllInsights')}</span>
                  </IonButton>
                </IonCardTitle>
              </IonCardHeader>
              <IonCardContent>
                {recentInsights?.length ? (
                  <div className="space-y-3">
                    {recentInsights.slice(0, 3).map((insight) => (
                      <div key={insight.id} className="bg-gray-50 p-3 rounded-lg">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center flex-1">
                            <Avatar
                              user={{
                                id: (insight as any).coachee?.id || insight.coachee_id,
                                full_name: (insight as any).coachee?.full_name || 'Unknown',
                                email: (insight as any).coachee?.email || '',
                                profile_picture_url: (insight as any).coachee?.profile_picture_url,
                                profile_picture_visibility: (insight as any).coachee?.profile_picture_visibility as any
                              }}
                              currentUserId={user?.id}
                              relationship="coach"
                              size="small"
                              className="w-6 h-6 mr-2"
                            />
                            <span className="text-sm font-medium text-gray-800">
                              {(insight as any).coachee?.full_name || t('dashboard.coach.unknownCoachee')}
                            </span>
                          </div>
                          <IonChip color="secondary" className="text-xs ml-2 flex-shrink-0">
                            {t('dashboard.coach.mood')}: {insight.mood}/10
                          </IonChip>
                        </div>
                        <p className="text-sm text-gray-600 line-clamp-2 mb-2">
                          {insight.notes || t('dashboard.coach.noNotesProvided')}
                        </p>
                        <div className="flex items-center justify-between">
                          <p className="text-xs text-gray-500">
                            {new Date(insight.date).toLocaleDateString()}
                          </p>
                          {insight.insights && (
                            <IonChip color="success" className="text-xs">
                              {t('dashboard.coach.hasInsights')}
                            </IonChip>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <IonIcon icon={journalOutline} className="text-4xl text-gray-400 mb-4" />
                    <p className="text-gray-500 mb-4 text-sm break-words">{t('dashboard.coach.noRecentInsights')}</p>
                    <p className="text-xs text-gray-400 break-words">{t('dashboard.coach.encourageReflections')}</p>
                  </div>
                )}
              </IonCardContent>
            </IonCard>
          </IonCol>
        </IonRow>

        {/* Quick Actions */}
        <IonRow>
          <IonCol size="12" className="px-2 sm:px-3">
            <IonCard>
              <IonCardHeader>
                <IonCardTitle className="text-base sm:text-lg break-words">{t('dashboard.coach.quickActions')}</IonCardTitle>
              </IonCardHeader>
              <IonCardContent>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                  <IonButton expand="block" fill="outline" className="h-14 sm:h-16" size="small" routerLink="/app/sessions">
                    <div className="text-center p-1">
                      <IonIcon icon={addOutline} className="block mb-1 text-lg sm:text-xl" />
                      <span className="text-xs break-words leading-tight">{t('dashboard.coach.newSession')}</span>
                    </div>
                  </IonButton>
                  
                  <IonButton expand="block" fill="outline" className="h-14 sm:h-16" size="small" onClick={() => setIsCoachManagementModalOpen(true)}>
                    <div className="text-center p-1">
                      <IonIcon icon={peopleOutline} className="block mb-1 text-lg sm:text-xl" />
                      <span className="text-xs break-words leading-tight">{t('dashboard.coach.manageClients')}</span>
                    </div>
                  </IonButton>
                  
                  <IonButton expand="block" fill="outline" className="h-14 sm:h-16" size="small" routerLink="/app/chat">
                    <div className="text-center p-1">
                      <IonIcon icon={chatbubbleOutline} className="block mb-1 text-lg sm:text-xl" />
                      <span className="text-xs break-words leading-tight">{t('dashboard.coach.sendMessage')}</span>
                    </div>
                  </IonButton>
                  
                  <IonButton expand="block" fill="outline" className="h-14 sm:h-16" size="small" routerLink="/app/sessions">
                    <div className="text-center p-1">
                      <IonIcon icon={statsChartOutline} className="block mb-1 text-lg sm:text-xl" />
                      <span className="text-xs break-words leading-tight">{t('dashboard.coach.viewReports')}</span>
                    </div>
                  </IonButton>
                </div>
              </IonCardContent>
            </IonCard>
          </IonCol>
        </IonRow>
      </IonGrid>
    </>
  )

  const renderCoacheeDashboard = () => (
    <>
      {/* Motivational Message */}
      <MotivationalWelcomeHeader 
        className="mb-4"
        additionalInfo={t('dashboard.coachee.continueJourney')}
      />

      <IonGrid className="px-0">
        {/* Your Recent Coaches */}
        <IonRow>
          <IonCol size="12" className="px-2 sm:px-3">
            <IonCard>
              <IonCardHeader>
                <IonCardTitle className="flex items-center justify-between text-base sm:text-lg">
                  <div className="flex items-center">
                    <IonIcon icon={linkOutline} className="mr-2 flex-shrink-0" />
                    {t('dashboard.coachee.yourRecentCoaches')}
                  </div>
                  <IonButton 
                    fill="clear" 
                    size="small"
                    routerLink="/app/profile#my-coaches"
                  >
                    <IonIcon icon={addOutline} slot="start" className="hidden sm:block" />
                    <IonIcon icon={ellipsisVerticalOutline} className="sm:hidden" />
                    <span className="hidden sm:inline ml-2">{t('dashboard.coachee.manage')}</span>
                  </IonButton>
                </IonCardTitle>
              </IonCardHeader>
              <IonCardContent>
                {coachRelationships.length > 0 ? (
                  <IonList className="ion-no-padding">
                    {coachRelationships.slice(0, 3).map((relationship) => (
                      <IonItem key={relationship.id} className="ion-no-padding">
                        <div className="flex items-center w-full py-2">
                          <div className="flex-shrink-0 mr-3">
                            <Avatar
                                                             user={{
                                 id: relationship.coach_id,
                                 full_name: relationship.coach_name,
                                 email: relationship.coach_email,
                                 profile_picture_url: relationship.coach_avatar_url,
                                 profile_picture_visibility: relationship.coach_profile_picture_visibility as 'public' | 'private' | 'none' | undefined
                               }}
                              currentUserId={user?.id}
                              relationship="coachee"
                              size="medium"
                              className="w-12 h-12 sm:w-14 sm:h-14"
                            />
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-1">
                              <h3 className="font-medium text-sm sm:text-base break-words leading-tight flex-1 min-w-0 mr-2">
                                {relationship.coach_name}
                              </h3>
                              <IonChip 
                                className="flex-shrink-0 text-xs"
                                color={relationship.status === 'active' ? 'success' : 
                                       relationship.status === 'pending' ? 'warning' : 'medium'}
                              >
                                {relationship.status === 'active' ? t('dashboard.coach.statusActive') :
                                 relationship.status === 'pending' ? t('dashboard.coach.statusPending') :
                                 t('dashboard.coach.statusInactive')}
                              </IonChip>
                            </div>
                            <p className="text-xs sm:text-sm text-gray-600 break-words">
                              {relationship.coach_specializations?.join(', ') || t('dashboard.coachee.generalCoaching')}
                            </p>
                          </div>
                        </div>
                      </IonItem>
                    ))}
                  </IonList>
                ) : (
                  <div className="text-center py-8">
                    <IonIcon icon={linkOutline} className="text-4xl text-gray-400 mb-4" />
                    <p className="text-gray-500 mb-4">{t('dashboard.coachee.noCoachesYet')}</p>
                    <IonButton 
                      fill="outline" 
                      size="small"
                      routerLink="/app/profile#my-coaches"
                    >
                      <IonIcon icon={linkOutline} slot="start" />
                      {t('dashboard.coachee.linkWithCoach')}
                    </IonButton>
                  </div>
                )}
              </IonCardContent>
            </IonCard>
          </IonCol>
        </IonRow>

        <IonRow>
          {/* Upcoming Sessions */}
          <IonCol size="12" sizeLg="6" className="px-2 sm:px-3">
            <IonCard className="h-full">
              <IonCardHeader>
                <IonCardTitle className="flex items-center justify-between text-base sm:text-lg">
                  <div className="flex items-center">
                    <IonIcon icon={calendarOutline} className="mr-2 flex-shrink-0" />
                    <span className="break-words">{t('dashboard.coachee.upcomingSessions')}</span>
                  </div>
                  <IonButton 
                    fill="clear" 
                    size="small"
                    routerLink="/app/sessions"
                  >
                    <IonIcon icon={ellipsisVerticalOutline} className="sm:hidden" />
                    <span className="hidden sm:inline">{t('dashboard.coachee.viewAllSessions')}</span>
                  </IonButton>
                </IonCardTitle>
              </IonCardHeader>
              <IonCardContent>
                {dashboardData.upcomingSessions?.length ? (
                  <div className="space-y-3">
                    {dashboardData.upcomingSessions.slice(0, 3).map((session) => (
                      <div key={session.id} className="bg-primary-50 p-3 rounded-lg">
                        <div className="flex items-start justify-between mb-2">
                          <h4 className="font-medium text-primary-900 text-sm sm:text-base break-words leading-tight flex-1">{session.title}</h4>
                          <IonChip 
                            color={session.status === 'scheduled' ? 'primary' : 'warning'} 
                            className="text-xs ml-2 flex-shrink-0"
                          >
                            {session.status.replace('_', ' ')}
                          </IonChip>
                        </div>
                        <div className="flex items-center mb-2">
                          <Avatar
                            user={{
                              id: (session as any).coach?.id || session.coach_id,
                              full_name: (session as any).coach?.full_name || t('dashboard.coach.unknownCoach'),
                              email: (session as any).coach?.email || '',
                              profile_picture_url: (session as any).coach?.profile_picture_url,
                              profile_picture_visibility: (session as any).coach?.profile_picture_visibility as any
                            }}
                            currentUserId={user?.id}
                            relationship="coachee"
                            size="small"
                            className="w-6 h-6 mr-2"
                          />
                          <span className="text-xs sm:text-sm text-primary-700 font-medium">
                            {t('dashboard.coachee.with')} {(session as any).coach?.full_name || t('dashboard.coachee.yourCoach')}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <p className="text-xs sm:text-sm text-primary-700">
                            {new Date(session.date).toLocaleDateString()} {t('dashboard.coachee.at')} {session.start_time}
                          </p>
                          <IonChip color="primary" className="text-xs">
                            {session.duration_minutes} {t('dashboard.coachee.min')}
                          </IonChip>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <IonIcon icon={calendarOutline} className="text-4xl text-gray-400 mb-4" />
                    <p className="text-gray-500 mb-4 text-sm break-words">{t('dashboard.coachee.noUpcomingSessions')}</p>
                    <IonButton 
                      fill="outline" 
                      size="small"
                      routerLink="/app/sessions"
                    >
                      <IonIcon icon={calendarOutline} slot="start" />
                      {t('dashboard.coachee.viewAllSessions')}
                    </IonButton>
                  </div>
                )}
              </IonCardContent>
            </IonCard>
          </IonCol>

          {/* Recent Mood & Progress */}
          <IonCol size="12" sizeLg="6" className="px-2 sm:px-3">
            <IonCard className="h-full">
              <IonCardHeader>
                <IonCardTitle className="flex items-center justify-between text-base sm:text-lg">
                  <div className="flex items-center">
                    <IonIcon icon={statsChartOutline} className="mr-2 flex-shrink-0" />
                    <span className="break-words">{t('dashboard.coachee.yourProgress')}</span>
                  </div>
                  <IonButton 
                    fill="clear" 
                    size="small"
                    routerLink="/app/goals"
                    routerDirection="forward"
                  >
                    <IonIcon icon={ellipsisVerticalOutline} />
                  </IonButton>
                </IonCardTitle>
              </IonCardHeader>
              <IonCardContent>
                {dashboardData.recentEntries?.length ? (
                  <>
                    <div className="mb-4">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-xs sm:text-sm font-medium break-words">{t('dashboard.coachee.latestMood')}</span>
                        <span className="text-lg sm:text-xl">
                          {(dashboardData.recentEntries[0]?.mood || 5) <= 2 ? '😢' :
                           (dashboardData.recentEntries[0]?.mood || 5) <= 4 ? '😟' :
                           (dashboardData.recentEntries[0]?.mood || 5) <= 6 ? '😐' :
                           (dashboardData.recentEntries[0]?.mood || 5) <= 8 ? '😊' : '😄'}
                        </span>
                      </div>
                      <div className="text-xl sm:text-2xl font-bold text-secondary-600 mb-1">
                        {dashboardData.recentEntries[0]?.mood || 5}/10
                      </div>
                                              <p className="text-xs sm:text-sm text-gray-600 break-words leading-relaxed">
                          {t('dashboard.coachee.on')} {new Date(dashboardData.recentEntries[0]?.date || new Date()).toLocaleDateString()}
                        </p>
                    </div>
                    
                    <div className="text-center">
                      <div className="text-base sm:text-lg font-semibold text-success-600">
                        {dashboardData.recentEntries.length}
                      </div>
                                              <div className="text-xs sm:text-sm text-gray-600 break-words">{t('dashboard.coachee.recentEntries')}</div>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-6">
                    <IonIcon icon={journalOutline} className="text-4xl text-gray-400 mb-3" />
                    <p className="text-gray-500 mb-4 text-sm">{t('dashboard.coachee.noReflectionEntriesYet')}</p>
                    <IonButton 
                      fill="outline" 
                      size="small"
                      routerLink="/app/goals"
                    >
                      <IonIcon icon={addOutline} slot="start" />
                      {t('dashboard.coachee.startReflecting')}
                    </IonButton>
                  </div>
                )}
              </IonCardContent>
            </IonCard>
          </IonCol>

        </IonRow>

        <IonRow>
          {/* Active Goals */}
          <IonCol size="12" sizeLg="6" className="px-2 sm:px-3">
            <IonCard className="h-full">
              <IonCardHeader>
                <IonCardTitle className="flex items-center justify-between text-base sm:text-lg">
                  <div className="flex items-center">
                    <IonIcon icon={trophyOutline} className="mr-2 flex-shrink-0" />
                    <span className="break-words">{t('dashboard.coachee.activeGoals')}</span>
                  </div>
                  <IonButton 
                    fill="clear" 
                    size="small"
                    routerLink="/app/goals?tab=goals"
                    routerDirection="forward"
                  >
                    <IonIcon icon={ellipsisVerticalOutline} />
                  </IonButton>
                </IonCardTitle>
              </IonCardHeader>
              <IonCardContent>
                {dashboardData.activeGoals?.length ? (
                  <div className="space-y-3">
                    {dashboardData.activeGoals.slice(0, 2).map((goal) => (
                      <div key={goal.id} className="bg-success-50 p-3 rounded-lg">
                        <div className="flex items-start justify-between mb-2">
                          <h4 className="font-medium text-success-900 text-sm sm:text-base break-words leading-tight flex-1">{goal.title}</h4>
                          <IonChip color="success" className="text-xs ml-2 flex-shrink-0">
                            {goal.category || t('dashboard.coachee.personal')}
                          </IonChip>
                        </div>
                        {goal.description && (
                          <p className="text-xs text-success-700 mb-2 break-words leading-relaxed">{goal.description}</p>
                        )}
                        <IonProgressBar value={(goal.progress || 0) / 100} color="success" className="mb-1" />
                        <div className="flex items-center justify-between">
                          <p className="text-xs sm:text-sm text-success-700">{goal.progress || 0}% {t('dashboard.coachee.complete')}</p>
                          {goal.target_date && (
                            <p className="text-xs text-success-600">
                              {t('dashboard.coachee.due')}: {new Date(goal.target_date).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <IonIcon icon={trophyOutline} className="text-4xl text-gray-400 mb-3" />
                    <p className="text-gray-500 mb-4 text-sm">{t('dashboard.coachee.noActiveGoalsYet')}</p>
                    <IonButton 
                      fill="outline" 
                      size="small"
                      routerLink="/app/goals"
                    >
                      <IonIcon icon={addOutline} slot="start" />
                      {t('dashboard.coachee.setFirstGoal')}
                    </IonButton>
                  </div>
                )}
              </IonCardContent>
            </IonCard>
          </IonCol>
        </IonRow>

        {/* Messages from Coach */}
        {dashboardData.unreadMessages?.length ? (
          <IonRow>
            <IonCol size="12" className="px-2 sm:px-3">
              <IonCard>
                <IonCardHeader>
                  <IonCardTitle className="flex items-center text-base sm:text-lg">
                    <IonIcon icon={notificationsOutline} className="mr-2 flex-shrink-0" />
                    <span className="break-words flex-1">{t('dashboard.coachee.messagesFromCoach')}</span>
                    <IonBadge color="primary" className="ml-2 text-xs">
                      {dashboardData.unreadMessages.length}
                    </IonBadge>
                  </IonCardTitle>
                </IonCardHeader>
                <IonCardContent>
                  <IonList>
                    {dashboardData.unreadMessages.slice(0, 3).map((message) => (
                      <IonItem key={message.id} className="rounded-lg mb-2">
                        <IonLabel>
                          <h3 className="font-medium text-sm sm:text-base break-words">{message.title || t('dashboard.coachee.newMessage')}</h3>
                          <p className="text-xs sm:text-sm text-gray-600 break-words leading-relaxed line-clamp-2">
                            {message.content}
                          </p>
                        </IonLabel>
                        <IonChip color={message.type === 'feedback' ? 'success' : 'primary'} className="text-xs flex-shrink-0">
                          {message.type}
                        </IonChip>
                      </IonItem>
                    ))}
                  </IonList>
                  <IonButton fill="clear" size="small" className="mt-4 text-xs sm:text-sm" routerLink="/app/messages">
                    <IonIcon icon={arrowForwardOutline} slot="end" />
                    <span className="break-words">{t('dashboard.coachee.viewAllMessages')}</span>
                  </IonButton>
                </IonCardContent>
              </IonCard>
            </IonCol>
          </IonRow>
        ) : null}

        {/* Quick Actions */}
        <IonRow>
          <IonCol size="12" className="px-2 sm:px-3">
            <IonCard>
              <IonCardHeader>
                <IonCardTitle className="text-base sm:text-lg break-words">{t('dashboard.coachee.quickActions')}</IonCardTitle>
              </IonCardHeader>
              <IonCardContent>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                  <IonButton expand="block" fill="outline" className="h-14 sm:h-16" size="small" routerLink="/app/goals">
                    <div className="text-center p-1">
                      <IonIcon icon={journalOutline} className="block mb-1 text-lg sm:text-xl" />
                      <span className="text-xs break-words leading-tight">{t('dashboard.coachee.newEntry')}</span>
                    </div>
                  </IonButton>
                  
                  <IonButton expand="block" fill="outline" className="h-14 sm:h-16" size="small" routerLink="/app/goals?tab=goals">
                    <div className="text-center p-1">
                      <IonIcon icon={trophyOutline} className="block mb-1 text-lg sm:text-xl" />
                      <span className="text-xs break-words leading-tight">{t('dashboard.coachee.newGoal')}</span>
                    </div>
                  </IonButton>
                  
                  <IonButton expand="block" fill="outline" className="h-14 sm:h-16" size="small" routerLink="/app/goals?tab=insights">
                    <div className="text-center p-1">
                      <IonIcon icon={statsChartOutline} className="block mb-1 text-lg sm:text-xl" />
                      <span className="text-xs break-words leading-tight">{t('dashboard.coachee.viewProgress')}</span>
                    </div>
                  </IonButton>
                  
                  <IonButton expand="block" fill="outline" className="h-14 sm:h-16" size="small" routerLink="/app/chat">
                    <div className="text-center p-1">
                      <IonIcon icon={chatbubbleOutline} className="block mb-1 text-lg sm:text-xl" />
                      <span className="text-xs break-words leading-tight">{t('dashboard.coachee.chatCoach')}</span>
                    </div>
                  </IonButton>
                </div>
              </IonCardContent>
            </IonCard>
          </IonCol>
        </IonRow>
      </IonGrid>

      {/* Floating Action Button for Quick Entry */}
      <IonFab vertical="bottom" horizontal="end" slot="fixed">
        <IonFabButton routerLink="/app/goals" color="secondary">
          <IonIcon icon={addOutline} />
        </IonFabButton>
      </IonFab>
    </>
  )

  if (loading) {
    return (
      <IonPage>
        <PageHeader 
          title={t('dashboard.dashboard')}
          showBackButton={false}
        >
          <div className="flex items-center gap-2 mr-2">
            <IonButton 
              fill="clear" 
              size="small"
              onClick={() => setIsThemeSwitcherOpen(true)}
              title={t('theme.customize')}
            >
              <IonIcon icon={colorPaletteOutline} />
            </IonButton>
          </div>
        </PageHeader>
        <IonContent>
          <div className="px-2 sm:px-4 py-4 flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
          </div>
        </IonContent>
        <ThemeSwitcher 
          isOpen={isThemeSwitcherOpen} 
          onClose={() => setIsThemeSwitcherOpen(false)} 
        />
      </IonPage>
    )
  }

  return (
    <IonPage>
      <PageHeader 
        title={isCoach() ? t('dashboard.coachDashboard') : t('dashboard.coacheeDashboard')}
        showBackButton={false}
      >
        <div className="flex items-center gap-2 mr-2">
          <IonButton 
            fill="clear" 
            size="small"
            onClick={() => setIsThemeSwitcherOpen(true)}
            title={t('theme.customize')}
          >
            <IonIcon icon={colorPaletteOutline} />
          </IonButton>
        </div>
      </PageHeader>
      
      <IonContent>
        <div className="px-2 sm:px-4 py-4">
          {isCoach() ? renderCoachDashboard() : renderCoacheeDashboard()}
        </div>
      </IonContent>

      {/* Theme Switcher Modal */}
      <ThemeSwitcher 
        isOpen={isThemeSwitcherOpen} 
        onClose={() => setIsThemeSwitcherOpen(false)} 
      />
    </IonPage>
  )
}

export default DashboardPage 